import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";

/**
 * When a control in `from` leaves the page with the focus in it (it went with what it acted on, or `from` went), the
 * focus goes to `to`, without scrolling, where the browser would drop it on the page. Only a focus that was in `from`:
 * a page whose focus is elsewhere, or nowhere, keeps it there. A function `to` names the stand-in for the control that
 * went.
 */
export function useFocusFallback(
  from: RefObject<HTMLElement | null>,
  to: RefObject<HTMLElement | null> | ((left: Element) => HTMLElement | null),
): void {
  const held = useRef<Element | null>(null);
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      held.current = e.target instanceof Element && from.current?.contains(e.target) ? e.target : null;
    };
    // A press on the page takes the focus from a control still there and free; one taken off the page, or disabled
    // under the focus, has not been left. A focus going to another control is followed by its focusin.
    const onFocusOut = (e: FocusEvent) => {
      const left = e.target;
      if (left !== held.current) return;
      queueMicrotask(() => {
        if (held.current === left && left instanceof Element && left.isConnected && !left.matches(":disabled"))
          held.current = null;
      });
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [from]);
  useLayoutEffect(() => {
    const left = held.current;
    if (left === null || left.isConnected) return;
    held.current = null;
    const active = document.activeElement;
    if (active === null || active === document.body)
      (typeof to === "function" ? to(left) : to.current)?.focus({ preventScroll: true });
  });
}
